import { Colors, Icon, MultilineInput, Typography } from '@amelio/ui-library';
import { type ReactNode, useEffect, useState } from 'react';

import { CharacterCount } from '@/pages/Competencies/components/profiles/CharacterCount';
import { getIconSvg } from '@/utils/iconSvgs';

const DESCRIPTION_MAX_LENGTH = 300;
export interface CompanyValueCardProps {
    icon?: string;
    cardBackgroundColor?: string;
    iconSize?: number;
    title: string;
    description?: string;
    badgeLabel?: string;
    badgeIcon?: ReactNode;
    metricLabel?: string;
    metricValue: number;
    metricIcon?: ReactNode;
    onClick?: () => void;
    isEditable?: boolean;
    onSave?: (updatedDescription: string, updatedMetricValue: number) => void;
    onDelete?: () => void;
    className?: string;
    dataTestId?: string;
    levelIcon?: string;
}

export const CompanyValueCard = ({
    icon,
    cardBackgroundColor = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    iconSize = 20,
    title,
    description,
    badgeLabel,
    badgeIcon,
    metricLabel,
    metricValue,
    metricIcon,
    onClick,
    isEditable = false,
    onSave,
    onDelete,
    className = '',
    dataTestId,
}: CompanyValueCardProps) => {
    const isClickable = !!onClick;
    const [isEditMode, setIsEditMode] = useState(false);
    const [editedDescription, setEditedDescription] = useState(description || '');
    const [editedMetricValue, setEditedMetricValue] = useState(metricValue);
    const [metricInputValue, setMetricInputValue] = useState(String(metricValue));

    useEffect(() => {
        if (!isEditMode) {
            setEditedDescription(description || '');
            setEditedMetricValue(metricValue);
            setMetricInputValue(String(metricValue));
        }
    }, [description, metricValue, isEditMode]);

    const handleEdit = () => {
        setIsEditMode(true);
        setEditedDescription(description || '');
        setEditedMetricValue(metricValue);
        setMetricInputValue(String(metricValue));
    };

    const handleSave = () => {
        if (onSave) {
            onSave(editedDescription, editedMetricValue);
        }
        setIsEditMode(false);
    };

    const isGradientOrColor =
        cardBackgroundColor.includes('gradient') ||
        cardBackgroundColor.startsWith('#') ||
        cardBackgroundColor.startsWith('rgb') ||
        cardBackgroundColor.startsWith('rgba') ||
        cardBackgroundColor.startsWith('hsl') ||
        cardBackgroundColor.startsWith('hsla') ||
        cardBackgroundColor.startsWith('var(');

    const baseClasses = `h-full group relative overflow-hidden rounded-[16px] pt-[32px] px-[12px] pb-[12px] min-h-[180px] sm:min-h-[200px] md:min-h-[210px] flex flex-col text-left w-full text-white ${!isGradientOrColor ? cardBackgroundColor : ''} ${className}`;
    const clickableClasses = isClickable
        ? 'cursor-pointer transition-transform hover:scale-[1.02] hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-turquoise focus:ring-offset-2'
        : '';

    // `getIconSvg` falls back to its own heart for an unknown id, so it always draws something.
    const renderIcon = () => getIconSvg(icon || 'star', iconSize);

    const CardContent = (
        <>
            {/* Status Badge */}
            {badgeLabel && (
                <div
                    className={
                        'absolute top-2 right-2 flex items-center gap-1.5 rounded-full border border-white/30 bg-white/20 px-[10px] py-[6px] backdrop-blur-md sm:top-3 sm:right-3'
                    }
                >
                    {badgeIcon || (
                        <Icon
                            name={'essential-checkmark'}
                            size={'xs'}
                            color={Colors.WHITE}
                        />
                    )}
                    <Typography
                        className={'text-caption! font-semibold text-white'}
                        color={Colors.WHITE}
                    >
                        {badgeLabel}
                    </Typography>
                </div>
            )}

            {/* Edit and Delete Buttons - appear on hover when editable */}
            {isEditable && !isEditMode && (
                <div
                    className={
                        'absolute top-2 right-2 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100 sm:top-3 sm:right-3'
                    }
                >
                    <button
                        type={'button'}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEdit();
                        }}
                        className={
                            'flex size-8 items-center justify-center rounded-lg border border-white/30 bg-white/90 backdrop-blur-md hover:bg-white'
                        }
                        aria-label={'Edit'}
                    >
                        <Icon
                            name={'money-icon_pencil'}
                            color={Colors.BLACK}
                        />
                    </button>
                    <button
                        type={'button'}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete?.();
                        }}
                        className={
                            'flex size-8 items-center justify-center rounded-lg border border-white/30 bg-white/90 backdrop-blur-md hover:bg-white'
                        }
                        aria-label={'Delete'}
                    >
                        <Icon
                            name={'essential-close'}
                            color={Colors.ERROR_800}
                        />
                    </button>
                </div>
            )}

            <div
                className={
                    'mb-3 grid grid-cols-[auto_1fr] items-center gap-x-[6px] gap-y-3 sm:mb-4'
                }
            >
                <div
                    className={
                        'flex shrink-0 items-center justify-center rounded-[8px] bg-white/45 p-[8px] backdrop-blur-sm'
                    }
                >
                    {renderIcon()}
                </div>
                <Typography
                    color={Colors.WHITE}
                    weight={'bold'}
                    className={'text-body!'}
                >
                    {title}
                </Typography>
                {!isEditMode && description && (
                    <Typography
                        className={'col-span-2'}
                        color={Colors.BASE_50}
                        variant={'body'}
                    >
                        {description}
                    </Typography>
                )}
            </div>
            {isEditMode && (
                <div className={'my-1'}>
                    <MultilineInput
                        value={editedDescription}
                        onChange={setEditedDescription}
                        className={
                            'w-full resize-none rounded-lg border border-white/30 bg-white/10 px-3 py-2 text-xs text-white placeholder-white/60 backdrop-blur-sm focus-within:border-white focus:ring-2! focus:ring-white focus:outline-none'
                        }
                        placeholder={'Enter description...'}
                        maxLength={DESCRIPTION_MAX_LENGTH}
                        rows={4}
                    />
                    <div className={'mt-2 flex justify-end'}>
                        <div
                            className={
                                'flex items-center justify-center rounded-full bg-white px-3 py-1.5'
                            }
                        >
                            <CharacterCount
                                value={editedDescription}
                                maxLength={DESCRIPTION_MAX_LENGTH}
                            />
                        </div>
                    </div>
                    <div className={'mt-3 flex gap-2'}>
                        <input
                            type={'number'}
                            value={metricInputValue}
                            onChange={(e) => {
                                const val = e.target.value;
                                setMetricInputValue(val);
                                const num = parseInt(val, 10);
                                if (!Number.isNaN(num)) {
                                    setEditedMetricValue(Math.min(100, Math.max(0, num)));
                                }
                            }}
                            onBlur={() => {
                                const num = parseInt(metricInputValue, 10);
                                const sanitized = Number.isNaN(num)
                                    ? 0
                                    : Math.min(100, Math.max(0, num));
                                setEditedMetricValue(sanitized);
                                setMetricInputValue(String(sanitized));
                            }}
                            className={
                                'text-caption flex-1 rounded-lg border border-white/30 bg-white/10 px-3 py-2 text-white backdrop-blur-sm focus:ring-2 focus:ring-white focus:outline-none'
                            }
                            min={0}
                            max={100}
                        />
                        <button
                            type={'button'}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSave();
                            }}
                            data-testid={'save-button'}
                            aria-label={'Save'}
                            className={
                                'text-foreground flex items-center justify-center gap-1 rounded-lg bg-white/90 px-4 py-2 text-sm font-semibold backdrop-blur-sm hover:shadow-lg'
                            }
                        >
                            <Icon
                                name={'essential-checkmark'}
                                color={Colors.BLACK}
                            />
                        </button>
                        <button
                            type={'button'}
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsEditMode(false);
                                setEditedDescription(description || '');
                                setEditedMetricValue(metricValue);
                                setMetricInputValue(String(metricValue));
                            }}
                            aria-label={'Cancel'}
                            className={
                                'text-foreground flex items-center justify-center gap-1 rounded-lg border border-white/40 bg-white/20 px-4 py-2 text-sm font-semibold backdrop-blur-sm hover:bg-white/30'
                            }
                        >
                            <Icon
                                name={'essential-close'}
                                color={Colors.WHITE}
                            />
                        </button>
                    </div>
                </div>
            )}

            {!isEditMode && (
                <div className={'mt-auto pb-[8px]'}>
                    <div className={'mb-[8px] flex items-center justify-between'}>
                        <div className={'flex items-center gap-1 sm:gap-1.5'}>
                            {metricIcon || (
                                <Icon
                                    name={'business-trend-up'}
                                    size={'xs'}
                                    color={Colors.WHITE}
                                />
                            )}
                            {metricLabel && (
                                <Typography
                                    className={'text-caption! font-bold text-white'}
                                    color={Colors.WHITE}
                                >
                                    {metricLabel}
                                </Typography>
                            )}
                        </div>
                        <Typography
                            weight={'bold'}
                            variant={'h3'}
                            color={Colors.WHITE}
                            className={'text-body-lg!'}
                        >
                            {`${Math.max(0, Math.min(100, metricValue))}%`}
                        </Typography>
                    </div>

                    <div className={'h-2 rounded-full bg-white/45'}>
                        <div
                            className={'h-2 rounded-full bg-white transition-all duration-300'}
                            style={{ width: `${Math.max(0, Math.min(100, metricValue))}%` }}
                            role={'progressbar'}
                            aria-valuenow={Math.max(0, Math.min(100, metricValue))}
                            aria-valuemin={0}
                            aria-valuemax={100}
                        />
                    </div>
                </div>
            )}
        </>
    );

    const cardStyle = isGradientOrColor
        ? { background: cardBackgroundColor }
        : { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' };

    if (isClickable) {
        return (
            <div
                role={'button'}
                tabIndex={0}
                className={`${baseClasses} ${clickableClasses}`}
                style={cardStyle}
                onClick={() => {
                    if (!isEditMode) onClick?.();
                }}
                onKeyDown={(e) => {
                    if (!isEditMode && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        onClick?.();
                    }
                }}
                data-testid={dataTestId}
            >
                {CardContent}
            </div>
        );
    }

    return (
        <div
            className={baseClasses}
            style={cardStyle}
            data-testid={dataTestId}
        >
            {CardContent}
        </div>
    );
};
